import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { useHistory, useLocation } from "react-router-dom";
import { Segment, Step, Icon } from "semantic-ui-react";
import { toast } from "react-toastify";
import NewEditField from "../NewEditField";
import FieldLayout from "../FieldLayout";
import FieldGpsSetup from "../FieldGpsSetup";
import FieldAdvancedAnalytics from "../FieldAdvancedAnalytics";
import ExperimentalDesigns from "../ExperimentalDesigns/index.tsx";

import { useModuleNavigation } from "../../../../hooks/useModuleNavigation";
import { useAuth0 } from "../../../../auth/auth0";
import * as fieldActions from "../../../../redux/actions/fieldActions";
import { updateFieldScaleTrialStatus } from "../../../../redux/actions/entityActions";

const FieldWizard = ({
	activeStep,
	mode,
	fromViewer,
	handleCancel,
	handleFinish,
	handleSave,
	fieldIdFromViewer = null,
}) => {
	const dispatch = useDispatch();
	const history = useHistory();
	const location = useLocation();
	const { getTokenSilently } = useAuth0();
	const moduleNavigation = useModuleNavigation();
	const entityNames = useSelector((state) => (state.entityNames ? state.entityNames : null));
	const clientId = useSelector((state) => (state.clients ? state.clients.currentId : null));

	const [step, setActiveStep] = useState(activeStep);
	const [modeState, setModeState] = useState(mode);
	const [fieldId, setFieldId] = useState(moduleNavigation.fieldId ?? fieldIdFromViewer);
	const [deleting, setDeleting] = useState(false);
	const [userSetFieldScaleTrial, setIsFieldScaleTrial] = useState(location.state?.isFieldScaleTrial);
	const isFieldScaleTrial = userSetFieldScaleTrial ?? entityNames?.fieldScaleTrial ?? false;
	const isFieldScaleTrialRef = useRef(isFieldScaleTrial);

	useEffect(() => {
		isFieldScaleTrialRef.current = isFieldScaleTrial;
	}, [isFieldScaleTrial]);

	useEffect(() => {
		return () => {
			if (!fromViewer) {
				dispatch(updateFieldScaleTrialStatus(isFieldScaleTrialRef.current));
			}
		};
	}, [dispatch, fromViewer]);

	//-- TODO: Add validation to block deleting if attached to a flight
	async function deleteField() {
		setDeleting(true);
		const accessToken = await getTokenSilently();
		dispatch(fieldActions.deleteField(fieldId, clientId, accessToken))
			.then(() => {
				setDeleting(false);
				toast.success("Field deleted successfully");
				if (fromViewer) {
					handleCancel();
				} else {
					history.push("/fields");
				}
			})
			.catch((err) => {
				console.log(err);
				toast.error("Field failed to delete");
				setDeleting(false);
			});
	}

	function navigateToInfo() {
		setActiveStep("info");
		if (!fromViewer) {
			history.push(moduleNavigation.createFieldLink(true, null, "edit"));
		}
	}

	function navigateToLayout(fieldId) {
		setActiveStep("layout");
		if (!fromViewer) {
			const fieldLink = fieldId
				? moduleNavigation.createFieldLink(null, fieldId, "layout")
				: moduleNavigation.createFieldLink(true, null, "layout");
			history.push(fieldLink);
		}
	}

	function navigateToGpsSetup() {
		setActiveStep("gps-setup");
		if (!fromViewer) {
			history.push(moduleNavigation.createFieldLink(true, null, "gps-setup"));
		}
	}

	function navigateToAdvancedAnalytics() {
		setActiveStep("advanced-analytics");
		if (!fromViewer) {
			history.push(moduleNavigation.createFieldLink(true, null, "advanced-analytics"));
		}
	}

	function navigateToExpDesigns(fieldId) {
		setActiveStep("experimental-designs");
		if (!fromViewer) {
			const fieldLink = fieldId
				? moduleNavigation.createFieldLink(null, fieldId, "experimental-designs")
				: moduleNavigation.createFieldLink(true, null, "experimental-designs");
			history.push(fieldLink);
		}
	}

	return (
		<Segment basic>
			<Step.Group widths={4}>
				<Step link active={step === "info"} onClick={() => navigateToInfo()}>
					<Icon name="clipboard list" />
					<Step.Content>
						<Step.Title>Field Info</Step.Title>
						<Step.Description>Field metadata</Step.Description>
					</Step.Content>
				</Step>
				{isFieldScaleTrial ? (
					<Step
						link
						active={step === "experimental-designs"}
						disabled={!fieldId}
						onClick={() => navigateToExpDesigns()}
					>
						<Icon name="table" />
						<Step.Content>
							<Step.Title>Experimental Designs</Step.Title>
							<Step.Description>Assign Plot Groupings</Step.Description>
						</Step.Content>
					</Step>
				) : (
					<Step link active={step === "layout"} disabled={!fieldId} onClick={() => navigateToLayout()}>
						<Icon name="table" />
						<Step.Content>
							<Step.Title>Field Layout</Step.Title>
							<Step.Description>Detailed plot randomizations</Step.Description>
						</Step.Content>
					</Step>
				)}
				<Step link active={step === "gps-setup"} disabled={!fieldId} onClick={() => navigateToGpsSetup()}>
					<Icon name="compass outline" />
					<Step.Content>
						<Step.Title>GPS Setup</Step.Title>
						<Step.Description>Define field position by GPS</Step.Description>
					</Step.Content>
				</Step>
				<Step
					link
					active={step === "advanced-analytics"}
					disabled={!fieldId}
					onClick={() => navigateToAdvancedAnalytics()}
				>
					<Icon name="chart bar outline" />
					<Step.Content>
						<Step.Title>Advanced Analytics</Step.Title>
						<Step.Description>Define areas for analytics</Step.Description>
					</Step.Content>
				</Step>
			</Step.Group>

			<Segment id="field-process-step-content" basic style={{ marginTop: "unset", padding: 0, minHeight: 70 }}>
				{(() => {
					switch (step) {
						default:
						case "info":
							return (
								<NewEditField
									mode={fieldId && modeState !== "new" ? "edit" : "new"}
									setModeState={(state) => setModeState(state)}
									setFieldId={(id) => setFieldId(id)}
									fieldId={fieldId}
									fromViewer={fromViewer}
									handleCancel={() => handleCancel()}
									onFieldConfigSaved={(isFieldScale) => setIsFieldScaleTrial(isFieldScale)}
									navigateToNextStep={navigateToLayout}
									navigateToExpDesigns={navigateToExpDesigns}
									isFieldScaleTrial={isFieldScaleTrial}
								/>
							);
						case "experimental-designs":
							return (
								<ExperimentalDesigns
									fieldId={fieldId}
									fromViewer={fromViewer}
									handleCancel={() => handleCancel()}
									handleDeleteField={() => deleteField()}
									isDeleting={deleting}
									navigateToNextStep={navigateToGpsSetup}
									navigateBack={navigateToInfo}
								/>
							);
						case "layout":
							return (
								<FieldLayout
									fieldId={fieldId}
									fromViewer={fromViewer}
									handleCancel={() => handleCancel()}
									navigateToNextStep={navigateToGpsSetup}
									navigateBack={navigateToInfo}
									handleDeleteField={() => deleteField()}
									isDeleting={deleting}
								/>
							);
						case "gps-setup":
							return (
								<FieldGpsSetup
									fieldId={fieldId}
									fromViewer={fromViewer}
									handleCancel={() => handleCancel()}
									navigateToNextStep={navigateToAdvancedAnalytics}
									navigateBack={navigateToLayout}
									handleDeleteField={() => deleteField()}
									isDeleting={deleting}
								/>
							);
						case "advanced-analytics":
							return (
								<FieldAdvancedAnalytics
									fieldId={fieldId}
									fromViewer={fromViewer}
									handleCancel={handleCancel}
									handleFinish={handleFinish}
									handleSave={handleSave}
									navigateBack={navigateToGpsSetup}
									handleDeleteField={() => deleteField()}
									isFieldScaleTrial={isFieldScaleTrial}
								/>
							);
					}
				})()}
			</Segment>
		</Segment>
	);
};

FieldWizard.propTypes = {
	activeStep: PropTypes.string.isRequired,
	mode: PropTypes.string.isRequired,
	fromViewer: PropTypes.bool,
	handleCancel: PropTypes.func,
	handleFinish: PropTypes.func,
	handleSave: PropTypes.func,
	fieldIdFromViewer: PropTypes.string,
};

export default FieldWizard;
